import { careIndustryPageKeys, type CareIndustryPageKey } from './careIndustryLabels.js';
import { contactMapConsentCopy, type ContactMapConsentCopy } from './contactMapContent.js';
import type { RetailIndustryPageKey } from './retailIndustryLabels.js';
import { industryApplicantHeroDefaults } from './industryApplicantHeroDefaults.js';
import { industryCompanyHeroDefaults } from './industryCompanyHeroDefaults.js';
import {
  applicationPortal,
  landingPageContentRepository,
  type LandingAction,
  type LandingFaqItem,
  type LandingLocale,
  type LandingSourceContent,
  type LandingTeamMemberContent,
} from './landingContent.js';
import industryCare from '../../img/hero/industry-care-v2.png';
import industryCraft from '../../img/hero/industry-craft-v2.png';
import industryGastro from '../../img/hero/industry-gastro-v2.png';
import industryRetail from '../../img/hero/industry-retail-v2.png';
import groupPhoto from '../../img/rnd/gruppenfoto.jpeg';
import partnerGlobus from '../../img/partner/globus.svg';
import globusGemeinsam from '../../img/partner/globusgemeinsam.jpeg';
import globusBakery from '../../img/globus-jobs/globus-bakery.webp';
import globusButcher from '../../img/globus-jobs/globus-butcher.webp';
import globusFreshCounter from '../../img/globus-jobs/globus-fresh-counter.webp';
import globusFreshCounterVideoPoster from '../../img/globus-jobs/globus-fresh-counter-video-poster.png';
import globusMarkthalle from '../../img/globus-campaign/globus_markthalle.jpg';
import missionImage from '../../img/mission/mission.jpeg';
import globusBakeryThumbnail from '../../img/globus-campaign/bäckerei6.webp';
import globusButcherThumbnail from '../../img/globus-campaign/metzger1.webp';
import globusGastronomyPoster from '../../img/globus-campaign/Gastro.webp';
import globusBakeryVideo from '../../video/globus/bakery/bakery.mp4';
import globusBakeryCaptionsDe from 'file:../../video/globus/bakery/vtt/Traskript Bäckerei Deutsch.vtt';
import globusBakeryCaptionsEn from 'file:../../video/globus/bakery/vtt/Traskript Bäckerei Englisch.vtt';
import globusBakeryCaptionsTr from 'file:../../video/globus/bakery/vtt/Traskript Bäckerei Türkisch.vtt';
import globusFreshCounterVideo from '../../video/globus/fresh-counter/cheese.mp4';
import globusFreshCounterCaptionsDe from 'file:../../video/globus/fresh-counter/vtt/Traskript Käsetheke Deutsch.vtt';
import globusFreshCounterCaptionsEn from 'file:../../video/globus/fresh-counter/vtt/Traskript Käsetheke Englisch.vtt';
import globusFreshCounterCaptionsTr from 'file:../../video/globus/fresh-counter/vtt/Traskript Käsetheke Türkisch.vtt';
import globusButcherVideo from '../../video/globus/butcher/metzger.mp4';
import globusButcherCaptionsDe from 'file:../../video/globus/butcher/vtt/Transkript Metzger Deutsch.vtt';
import globusButcherCaptionsEn from 'file:../../video/globus/butcher/vtt/Transkript Metzger Englisch.vtt';
import globusButcherCaptionsTr from 'file:../../video/globus/butcher/vtt/Transkript Metzger Türkisch.vtt';
import globusGastronomyVideo from '../../video/globus/gastronomy/gastro.mp4';
import globusGastronomyCaptionsDe from 'file:../../video/globus/gastronomy/vtt/Traskribt Gastro Deutsch.vtt';
import globusGastronomyCaptionsEn from 'file:../../video/globus/gastronomy/vtt/Traskribt Gastro Englisch.vtt';
import globusGastronomyCaptionsTr from 'file:../../video/globus/gastronomy/vtt/Traskribt Gastro Tuerkisch.vtt';
import { createStaticPageApplicantDefaults } from './staticPageApplicantDefaults.js';
import { createStaticPageCompanyDefaults } from './staticPageCompanyDefaults.js';
import { industryGalleryDefaults, industryOverviewCopy } from './industryPageContent.js';
import { isIndustryPageKey, type NewIndustryPageKey } from './industryPages.js';
import {
  createGermanGlobusJobs,
  germanGlobusJobsOverview,
  type GlobusJobContent,
  type GlobusJobsOverviewContent,
} from './globusJobContent.js';

export type StaticPageKey =
  | CareIndustryPageKey
  | RetailIndustryPageKey
  | NewIndustryPageKey
  | 'bewerben'
  | 'unternehmen'
  | 'pflege'
  | 'gastro'
  | 'handwerk'
  | 'einzelhandel'
  | 'globus'
  | 'ablauf'
  | 'ueber-vivaporta'
  | 'team'
  | 'kontakt'
  | 'impressum';
export type StaticPageAudience = 'applicant' | 'company';

export const staticPageKeys: readonly StaticPageKey[] = [
  ...careIndustryPageKeys,
  'bewerben',
  'unternehmen',
  'pflege',
  'gastro',
  'handwerk',
  'einzelhandel',
  'elektriker',
  'bauwesen',
  'schweisser',
  'cnc',
  'shk',
  'lkw-fahrer',
  'gastronomie', 'baecker', 'metzger', 'frischetheke',
  'globus',
  'ablauf',
  'ueber-vivaporta',
  'team',
  'kontakt',
  'impressum',
];

export interface StaticPageCardContent {
  iconClass: string;
  title: string;
  text: string;
  image?: string;
  imageAlt?: string;
}

export interface StaticPageSectionContent {
  eyebrow: string;
  title: string;
  titleHighlight?: string;
  text: string;
  image?: string;
  imageAlt?: string;
  cards?: StaticPageCardContent[];
}

export interface StaticPageHeroGalleryItem {
  image: string;
  imageAlt: string;
  title?: string;
  kind?: 'photo' | 'logo';
}

export interface StaticMissionStoryTextPart {
  text: string;
  strong?: boolean;
}

export interface StaticMissionStoryParagraphContent {
  text?: string;
  parts: StaticMissionStoryTextPart[];
}

export interface StaticMissionStoryBlockContent {
  title: string;
  paragraphs: StaticMissionStoryParagraphContent[];
}

export interface StaticMissionStoryContent {
  eyebrow: string;
  title: string;
  slogan: string;
  intro: string;
  blocks: StaticMissionStoryBlockContent[];
  closing: {
    title: string;
    paragraphs: StaticMissionStoryParagraphContent[];
  };
}

export interface StaticPageContent {
  key: StaticPageKey;
  eyebrow: string;
  title: string;
  heroTitleHighlight?: string;
  heroBackgroundWord?: string;
  intro: string;
  heroStageTitle?: string;
  heroImage?: string;
  heroGallery?: StaticPageHeroGalleryItem[];
  // Optional hero video (industry pages). Editable in the editor; falls back to the image poster.
  heroVideo?: string;
  // Optional short value-prop chips shown in the immersive industry hero. Optional so existing pages
  // and published projects keep working unchanged when a page has none.
  heroHighlights?: string[];
  // Optional primary CTA revealed in the scroll-driven industry hero (decoupled from the page's bottom
  // CTA so it gets its own editable, unique editor field).
  heroAction?: LandingAction;
  cardsEyebrow?: string;
  cardsTitle?: string;
  cards: StaticPageCardContent[];
  sections?: StaticPageSectionContent[];
  faq?: LandingFaqItem[];
  cta?: {
    eyebrow: string;
    title: string;
    text: string;
    image?: string;
    imageAlt?: string;
    primaryAction: LandingAction;
    secondaryAction: LandingAction;
  };
  sources?: LandingSourceContent[];
  team?: LandingTeamMemberContent[];
  missionStory?: StaticMissionStoryContent;
  globusJobs?: GlobusJobContent[];
  globusJobsOverview?: GlobusJobsOverviewContent;
  contact?: {
    eyebrow: string;
    title: string;
    email: string;
    phone: string;
    address: string;
    text: string;
    mapEmbedUrl: string;
    mapConsent?: Partial<ContactMapConsentCopy>;
  };
}

type IndustryPageKey = Extract<StaticPageKey, 'pflege' | 'gastro' | 'handwerk' | 'einzelhandel'>;
type IndustryHeroLocalizedContent = Pick<StaticPageContent, 'eyebrow' | 'title' | 'intro' | 'heroStageTitle' | 'heroHighlights'> & {
  heroActionLabel: string;
};

export const globusJobCards: StaticPageCardContent[] = [
  {
    iconClass: 'icon-[material-symbols--bakery-dining]',
    title: 'Bäcker:in in Produktion und Verkauf',
    text:
      'Für die Backbereiche sucht Globus Menschen, die morgens mit anpacken, Ware sauber vorbereiten und Kundinnen und Kunden im Verkauf freundlich beraten.',
    image: globusBakery,
    imageAlt: 'Bäckereimitarbeiterin in einem modernen Supermarkt arrangiert frische Brote',
  },
  {
    iconClass: 'icon-[material-symbols--set-meal]',
    title: 'Metzger:in in Produktion, Herstellung und Verkauf',
    text:
      'Gesucht werden Mitarbeitende mit handwerklichem Verständnis für Zuschnitt, Herstellung, Thekenpräsentation und Beratung rund um Fleisch- und Wurstwaren.',
    image: globusButcher,
    imageAlt: 'Metzger arbeitet konzentriert in einer hellen Supermarkt-Fleischabteilung',
  },
  {
    iconClass: 'icon-[material-symbols--storefront]',
    title: 'Mitarbeiter:in für Frischetheken',
    text:
      'Für Käse, Fisch und Fleisch zählen Produktgefühl, Hygiene, Kommunikation und Freude daran, Kundinnen und Kunden direkt am Tresen zu begleiten.',
    image: globusFreshCounter,
    imageAlt: 'Mitarbeiter berät eine Kundin an einer Frischetheke mit Käse und Fisch',
  },
];

const globusJobsSection = (): StaticPageSectionContent => ({
  eyebrow: 'Aktuell gesucht',
  title: 'Aktuelle Stellenausschreibungen bei GLOBUS',
  text:
    'Die offenen Rollen sind nah am Produkt und nah am Menschen: Backwaren, Metzgerei und Frischetheken. Genau hier entsteht ein Einstieg, bei dem internationale Mitarbeitende ihre praktische Erfahrung sichtbar einbringen können.',
  cards: globusJobCards,
});

const globusJobs = createGermanGlobusJobs({
  applicationHref: profession => applicationPortal.globusProfession(profession),
  bakeryVideo: globusBakeryVideo,
  bakeryCaptionTracks: [
    { src: globusBakeryCaptionsDe, srclang: 'de', label: 'Deutsch' },
    { src: globusBakeryCaptionsEn, srclang: 'en', label: 'Englisch' },
    { src: globusBakeryCaptionsTr, srclang: 'tr', label: 'Türkisch' },
  ],
  butcherVideo: globusButcherVideo,
  butcherCaptionTracks: [
    { src: globusButcherCaptionsDe, srclang: 'de', label: 'Deutsch' },
    { src: globusButcherCaptionsEn, srclang: 'en', label: 'Englisch' },
    { src: globusButcherCaptionsTr, srclang: 'tr', label: 'Türkisch' },
  ],
  gastronomyVideo: globusGastronomyVideo,
  gastronomyCaptionTracks: [
    { src: globusGastronomyCaptionsDe, srclang: 'de', label: 'Deutsch' },
    { src: globusGastronomyCaptionsEn, srclang: 'en', label: 'Englisch' },
    { src: globusGastronomyCaptionsTr, srclang: 'tr', label: 'Türkisch' },
  ],
  freshCounterVideo: globusFreshCounterVideo,
  freshCounterCaptionTracks: [
    { src: globusFreshCounterCaptionsDe, srclang: 'de', label: 'Deutsch' },
    { src: globusFreshCounterCaptionsEn, srclang: 'en', label: 'Englisch' },
    { src: globusFreshCounterCaptionsTr, srclang: 'tr', label: 'Türkisch' },
  ],
  bakeryPoster: globusBakeryThumbnail,
  butcherPoster: globusButcherThumbnail,
  freshCounterPoster: globusFreshCounterVideoPoster,
  gastronomyPoster: globusGastronomyPoster,
});

const industryPageKeys = new Set<StaticPageKey>(['pflege', 'gastro', 'handwerk', 'einzelhandel']);

const industryHeroLocaleContent: Record<
  Exclude<LandingLocale, 'de'>,
  Record<StaticPageAudience, Record<IndustryPageKey, IndustryHeroLocalizedContent>>
> = {
  tr: {
    applicant: {
      pflege: {
        eyebrow: 'Bakım sektörü',
        title: 'Bakımın geleceği insanla başlar.',
        intro: 'Almanya’da bakım alanında çalışmak mı istiyorsun? Dil hazırlığı, diploma denkliği ve deneyimine uygun bir ekibe katılma sürecinde sana eşlik ediyoruz.',
        heroStageTitle: 'Almanya’da bakım.',
        heroHighlights: ['Denklik desteği', 'Mesleki Almanca', 'Kişisel rehberlik'],
        heroActionLabel: 'Hemen başvur',
      },
      gastro: {
        eyebrow: 'Gastro sektörü',
        title: 'Güçlü servis ekiplerinde yeni başlangıcın.',
        intro: 'Mutfakta ya da serviste çalışıyorsan Almanya’da sana uygun bir ekip bulmana, dil hazırlığına ve yeni işine uyum sağlamana destek oluyoruz.',
        heroStageTitle: 'Servis ekibinde başlangıç.',
        heroHighlights: ['Servis ve mutfak', 'Ekip uyumu', 'Dil hazırlığı'],
        heroActionLabel: 'Hemen başvur',
      },
      handwerk: {
        eyebrow: 'Zanaat sektörü',
        title: 'Zanaatte geleceği olan bir yol kur.',
        intro: 'Mesleki becerilerin Almanya’da yeni kapılar açabilir. Deneyimine uygun bir işletme bulmana ve iş hayatına hazırlanmana destek oluyoruz.',
        heroStageTitle: 'Zanaate giden yolun.',
        heroHighlights: ['Nitelik değerlendirmesi', 'Uygun işletme', 'Uyum desteği'],
        heroActionLabel: 'Hemen başvur',
      },
      einzelhandel: {
        eyebrow: 'Perakende sektörü',
        title: 'Perakende. Hizmet, tazelik ve müşteri yakınlığı.',
        intro: 'Müşterilerle iletişimi ve taze ürünlerle çalışmayı seviyor musun? Almanya’da satış, müşteri danışmanlığı veya taze ürün reyonlarında işe başlamana destek oluyoruz.',
        heroStageTitle: 'Perakendede başlangıç.',
        heroHighlights: ['Müşteri iletişimi', 'Taze ürün alanları', 'Kalıcı başlangıç'],
        heroActionLabel: 'Hemen başvur',
      },
    },
    company: {
      pflege: {
        eyebrow: 'Bakım için sektör çözümü',
        title: 'Bakımın geleceği insanla başlar.',
        intro: 'Bakım kuruluşlarını uluslararası uzmanlarla buluşturuyoruz. Dil hazırlığı, denklik ve ekibe katılım sürecini kişisel destekle birlikte planlıyoruz.',
        heroStageTitle: 'Kalıcı bakım ekipleri.',
        heroHighlights: ['Dil hazırlığı', 'Denklik desteği', 'Kişisel entegrasyon'],
        heroActionLabel: 'İhtiyaç bildir',
      },
      gastro: {
        eyebrow: 'Gastro için sektör çözümü',
        title: 'Temponuza uyum sağlayan gastro ekipleri.',
        intro: 'Mutfak ve servis ekibiniz için uluslararası çalışanlar bulmanıza yardımcı oluyoruz. Görevleri, dil gereksinimlerini ve ekip beklentilerini baştan netleştiriyoruz.',
        heroStageTitle: 'Temponuza uyan ekipler.',
        heroHighlights: ['Servis odaklılık', 'Ekip çalışması', 'Dil hazırlığı'],
        heroActionLabel: 'İhtiyaç bildir',
      },
      handwerk: {
        eyebrow: 'Zanaat için sektör çözümü',
        title: 'Yarının zanaati için doğru çalışanlar.',
        intro: 'İşletmenize uygun uluslararası ustaları bulmanıza yardımcı oluyoruz. Mesleki yeterlilikleri, dil becerilerini ve işe hazırlık sürecini birlikte değerlendiriyoruz.',
        heroStageTitle: 'Zanaat için uzmanlar.',
        heroHighlights: ['Yapılandırılmış seçim', 'Uzun vadeli bağlılık', 'Entegrasyon desteği'],
        heroActionLabel: 'İhtiyaç bildir',
      },
      einzelhandel: {
        eyebrow: 'Perakende için sektör çözümü',
        title: 'Perakendeyi güçlendiren insanlar.',
        intro: 'Satış, müşteri danışmanlığı ve taze ürün reyonları için uluslararası çalışanlar bulmanıza ve ekibinize katılımlarını planlamanıza destek oluyoruz.',
        heroStageTitle: 'Mağazaları güçlendiren ekipler.',
        heroHighlights: ['Müşteri odaklılık', 'Taze ürün alanları', 'Kalıcı entegrasyon'],
        heroActionLabel: 'İhtiyaç bildir',
      },
    },
  },
  en: {
    applicant: {
      pflege: {
        eyebrow: 'Care industry',
        title: 'Care needs people with a future.',
        intro: 'Want to work in care in Germany? We support you with language preparation, qualification recognition and finding a care team that fits your experience.',
        heroStageTitle: 'Care in Germany.',
        heroHighlights: ['Recognition support', 'Professional German', 'Personal guidance'],
        heroActionLabel: 'Apply now',
      },
      gastro: {
        eyebrow: 'Hospitality industry',
        title: 'Your start in strong service teams.',
        intro: 'Whether you work in the kitchen or in service, we help you find a suitable team in Germany and prepare for the language and work involved.',
        heroStageTitle: 'Your start in service.',
        heroHighlights: ['Service and kitchen', 'Team fit', 'Language preparation'],
        heroActionLabel: 'Apply now',
      },
      handwerk: {
        eyebrow: 'Trades industry',
        title: 'Build a future-ready path in the trades.',
        intro: 'Your practical skills can open doors in Germany. We help you find an employer that fits your experience and prepare for your first day at work.',
        heroStageTitle: 'Your path into the trades.',
        heroHighlights: ['Qualification review', 'Employer fit', 'Arrival support'],
        heroActionLabel: 'Apply now',
      },
      einzelhandel: {
        eyebrow: 'Retail industry',
        title: 'Retail. Service, fresh goods and customer closeness.',
        intro: 'Enjoy helping customers and working with fresh products? We support your move into sales, customer service or fresh food counters in Germany.',
        heroStageTitle: 'Your start in retail.',
        heroHighlights: ['Customer contact', 'Fresh departments', 'Long-term start'],
        heroActionLabel: 'Apply now',
      },
    },
    company: {
      pflege: {
        eyebrow: 'Care industry solution',
        title: 'Care needs people with a future.',
        intro: 'We connect care providers with international professionals and personally support language preparation, recognition and the start in your team.',
        heroStageTitle: 'Care teams that stay.',
        heroHighlights: ['Language preparation', 'Recognition support', 'Personal integration'],
        heroActionLabel: 'Submit staffing need',
      },
      gastro: {
        eyebrow: 'Hospitality industry solution',
        title: 'Hospitality teams that keep pace.',
        intro: 'We help you find international staff for kitchen and service roles, clarifying tasks, language skills and team expectations from the outset.',
        heroStageTitle: 'Teams that keep pace.',
        heroHighlights: ['Service mindset', 'Team capability', 'Language preparation'],
        heroActionLabel: 'Submit staffing need',
      },
      handwerk: {
        eyebrow: 'Trades industry solution',
        title: 'Skilled people for tomorrow’s trades.',
        intro: 'We help you find international professionals for your trade business, considering their qualifications, language skills and preparation for everyday work.',
        heroStageTitle: 'Skilled people for trades.',
        heroHighlights: ['Structured selection', 'Long-term retention', 'Guided integration'],
        heroActionLabel: 'Submit staffing need',
      },
      einzelhandel: {
        eyebrow: 'Retail industry solution',
        title: 'People who strengthen retail locations.',
        intro: 'From sales and customer service to fresh food counters, we help you find international staff and support their start in your team.',
        heroStageTitle: 'People strengthening stores.',
        heroHighlights: ['Customer focus', 'Fresh departments', 'Long-term integration'],
        heroActionLabel: 'Submit staffing need',
      },
    },
  },
};

export const localizeIndustryHeroPage = (
  page: StaticPageContent,
  pageKey: StaticPageKey,
  audience: StaticPageAudience,
  locale: LandingLocale,
): StaticPageContent => {
  const forAudience = (value: StaticPageContent) => audience === 'company' ? industryCompanyHeroDefaults(value, locale) : industryApplicantHeroDefaults(value, locale);
  if (!industryPageKeys.has(pageKey)) return forAudience(page);
  const industryPageKey = pageKey as IndustryPageKey;
  const pageWithoutVideo = { ...page };
  delete pageWithoutVideo.heroVideo;
  if (locale === 'de') return forAudience(pageWithoutVideo);

  const translated = industryHeroLocaleContent[locale]?.[audience]?.[industryPageKey];
  if (!translated) return forAudience(pageWithoutVideo);
  return forAudience({
    ...pageWithoutVideo,
    eyebrow: translated.eyebrow,
    title: translated.title,
    intro: translated.intro,
    heroStageTitle: translated.heroStageTitle,
    heroHighlights: [...(translated.heroHighlights ?? [])],
    heroAction: page.heroAction ? { ...page.heroAction, label: translated.heroActionLabel } : page.heroAction,
  });
};

type ContactLocalizedContent = Pick<StaticPageContent, 'eyebrow' | 'title' | 'intro' | 'cards'> & {
  contactEyebrow: string;
  contactTitle: string;
  contactText: string;
};

const contactLocaleContent: Record<
  Exclude<LandingLocale, 'de'>,
  Record<StaticPageAudience, ContactLocalizedContent>
> = {
  tr: {
    applicant: {
      eyebrow: 'Doğrudan iletişim',
      title: 'Soruların mı var? Konuşalım.',
      intro:
        'Hedefini anlat. Sonraki adımları birlikte netleştirelim.',
      cards: [
        { iconClass: 'icon-[material-symbols--mail]', title: 'E-posta', text: 'info@vivaporta.org' },
        { iconClass: 'icon-[material-symbols--call]', title: 'Telefon', text: '+49 176 42887719' },
        { iconClass: 'icon-[material-symbols--location-on]', title: 'Adres', text: 'Mittelstraße 81, 44575 Castrop-Rauxel' },
      ],
      contactEyebrow: 'Kişisel danışmanlık',
      contactTitle: 'Bize ulaşın',
      contactText:
        'Bize eğitimin, mevcut Almanca seviyen ve Almanya’daki mesleki hedefin hakkında kısaca bilgi ver. Sana dürüst bir değerlendirme ve başvuru, dil, denklik ve giriş süreci için net bir yol haritası sunalım.',
    },
    company: {
      eyebrow: 'Doğrudan iletişim',
      title: 'Uzman mı arıyorsunuz? Konuşalım.',
      intro:
        'Aradığınız profili anlatın. Sonraki adımları birlikte planlayalım.',
      cards: [
        { iconClass: 'icon-[material-symbols--mail]', title: 'E-posta', text: 'info@vivaporta.org' },
        { iconClass: 'icon-[material-symbols--call]', title: 'Telefon', text: '+49 176 42887719' },
        { iconClass: 'icon-[material-symbols--location-on]', title: 'Adres', text: 'Mittelstraße 81, 44575 Castrop-Rauxel' },
      ],
      contactEyebrow: 'Kişisel danışmanlık',
      contactTitle: 'Bize ulaşın',
      contactText:
        'Aradığınız rolü, kişi sayısını, başlangıç zamanını ve özel gereksinimleri kısaca paylaşın. Size süre, hazırlık, entegrasyon ve sonraki adımlar hakkında gerçekçi bir değerlendirme sunalım.',
    },
  },
  en: {
    applicant: {
      eyebrow: 'Direct contact',
      title: 'Questions? Let’s talk.',
      intro:
        'Tell us your goal. We will clarify the next steps together.',
      cards: [
        { iconClass: 'icon-[material-symbols--mail]', title: 'Email', text: 'info@vivaporta.org' },
        { iconClass: 'icon-[material-symbols--call]', title: 'Phone', text: '+49 176 42887719' },
        { iconClass: 'icon-[material-symbols--location-on]', title: 'Address', text: 'Mittelstraße 81, 44575 Castrop-Rauxel' },
      ],
      contactEyebrow: 'Personal guidance',
      contactTitle: 'Contact us',
      contactText:
        'Tell us briefly about your qualification, current German level and professional goal. We will respond with an honest assessment and clear next steps for your application, language, recognition and entry process.',
    },
    company: {
      eyebrow: 'Direct contact',
      title: 'Need specialists? Let’s talk.',
      intro:
        'Tell us who you need. We will plan the next steps together.',
      cards: [
        { iconClass: 'icon-[material-symbols--mail]', title: 'Email', text: 'info@vivaporta.org' },
        { iconClass: 'icon-[material-symbols--call]', title: 'Phone', text: '+49 176 42887719' },
        { iconClass: 'icon-[material-symbols--location-on]', title: 'Address', text: 'Mittelstraße 81, 44575 Castrop-Rauxel' },
      ],
      contactEyebrow: 'Personal guidance',
      contactTitle: 'Contact us',
      contactText:
        'Tell us the role, number of specialists, preferred start date and any specific requirements. We will provide a realistic assessment of timing, preparation, integration and the next practical steps.',
    },
  },
};

export const localizeContactPage = (
  page: StaticPageContent,
  audience: StaticPageAudience,
  locale: LandingLocale,
): StaticPageContent => {
  if (page.key !== 'kontakt') return page;
  page = {
    ...page,
    contact: page.contact ? { ...page.contact, mapConsent: contactMapConsentCopy(locale, page.contact.mapConsent) } : page.contact,
  };
  if (locale === 'de') return page;
  const translated = contactLocaleContent[locale]?.[audience];
  if (!translated) return page;
  return {
    ...page,
    eyebrow: translated.eyebrow,
    title: translated.title,
    intro: translated.intro,
    cards: translated.cards.map(card => ({ ...card })),
    contact: page.contact
      ? {
          ...page.contact,
          eyebrow: translated.contactEyebrow,
          title: translated.contactTitle,
          text: translated.contactText,
        }
      : page.contact,
  };
};

const missionParagraph = (...parts: StaticMissionStoryTextPart[]): StaticMissionStoryParagraphContent => ({
  text: parts.map(part => part.text).join(''),
  parts,
});
const textPart = (text: string): StaticMissionStoryTextPart => ({ text });
const strongPart = (text: string): StaticMissionStoryTextPart => ({ text, strong: true });

const missionStoryContent: StaticMissionStoryContent = {
  eyebrow: 'Unsere Geschichte',
  title: 'Grenzen überwinden, Menschen verbinden.',
  slogan: 'Personaldienstleistung beginnt beim Menschen.',
  intro:
    'Viva Porta wurde 2025 mit einer klaren Überzeugung gegründet. Aus deutsch-türkischen Wurzeln, unternehmerischer Erfahrung und sozialer Verantwortung ist eine Vermittlung entstanden, die Menschen nicht als Profile betrachtet.',
  blocks: [
    {
      title: 'Der Anfang',
      paragraphs: [
        missionParagraph(
          textPart('Viva Porta wurde 2025 mit einer klaren Überzeugung gegründet: Erfolgreiche Personaldienstleistung beginnt beim '),
          strongPart('Menschen'),
          textPart('.'),
        ),
        missionParagraph(
          textPart('Durch unsere Wurzeln in Deutschland und der Türkei verstehen wir beide Kulturen aus eigener Erfahrung und schaffen Verbindungen zwischen Menschen und Unternehmen über Grenzen hinweg. So schlagen wir eine '),
          strongPart('Brücke'),
          textPart(' zwischen zwei Ländern und eröffnen neue Perspektiven für nachhaltige Zusammenarbeit.'),
        ),
        missionParagraph(
          textPart('Aufbauend auf einem Netzwerk, das uns über viele Jahre in wirtschaftlichen und gesellschaftlichen Kontexten sein Vertrauen geschenkt hat, verfolgen wir eine klare Mission: dem Fachkräftemangel nachhaltig zu begegnen, Unternehmen in Deutschland mit engagierten und qualifizierten Mitarbeitenden zu unterstützen und Menschen, die hier eine berufliche Zukunft aufbauen möchten, neue Perspektiven zu eröffnen.'),
        ),
      ],
    },
    {
      title: 'Die Menschen dahinter',
      paragraphs: [
        missionParagraph(
          textPart('Die Gründer '),
          strongPart('Wilhelm Josten'),
          textPart(' und '),
          strongPart('Enes Vural'),
          textPart(' vereinen unternehmerische Erfahrung, Innovationskraft und den festen Glauben daran, dass jedes Unternehmen nur so stark ist wie die '),
          strongPart('Menschen'),
          textPart(', die es tragen.'),
        ),
        missionParagraph(
          textPart('Unterstützt werden sie dabei von '),
          strongPart('Murat Vural'),
          textPart(', der seine langjährige Erfahrung als '),
          strongPart('Sozialunternehmer'),
          textPart(' in die Entwicklung von Viva Porta einbringt.'),
        ),
      ],
    },
    {
      title: 'Unternehmerische und soziale Erfahrung',
      paragraphs: [
        missionParagraph(
          strongPart('Wilhelm Josten'),
          textPart(' stammt aus einer traditionsreichen Unternehmerfamilie und hat mit der erfolgreichen Einrichtungskette '),
          strongPart('BUTLERS'),
          textPart(' eine Marke geschaffen, die heute deutschlandweit bekannt ist. Sein Gespür für Märkte, seine Innovationskraft und sein Verständnis für nachhaltiges Wachstum prägen die strategische Ausrichtung von Viva Porta.'),
        ),
        missionParagraph(
          strongPart('Murat Vural'),
          textPart(', Diplom-Ingenieur der Elektrotechnik, engagiert sich seit über zwei Jahrzehnten für gerechte Bildungschancen. Als Träger des '),
          strongPart('Bundesverdienstkreuzes am Bande'),
          textPart(' und Gründer und Leiter eines gemeinnützigen '),
          strongPart('Sozialunternehmens'),
          textPart(' setzt er sich dafür ein, Menschen Perspektiven zu eröffnen und Potenziale zu fördern.'),
        ),
        missionParagraph(
          textPart('Für Murat Vural steht der Mensch stets im Mittelpunkt seines Handelns. Er ist überzeugt, dass nachhaltiger Unternehmenserfolg nur durch ein starkes '),
          strongPart('Team'),
          textPart(' entstehen kann. Deshalb hat er seine Entscheidungen immer '),
          strongPart('sinnorientiert'),
          textPart(' getroffen.'),
        ),
      ],
    },
    {
      title: 'Operative Nähe',
      paragraphs: [
        missionParagraph(
          textPart('Als Geschäftsführer verantwortet '),
          strongPart('Enes Vural'),
          textPart(' die operative und strategische Entwicklung von Viva Porta. Seine Erfahrungen im Vertrieb, seine langjährige Tätigkeit im gemeinnützigen Bereich und sein ausgeprägtes Verständnis für Menschen bilden das Fundament seines Handelns. Für ihn steht fest: Hinter jeder Bewerbung steht eine persönliche Geschichte. Hinter jeder offenen Stelle stehen Ziele, Verantwortung und Zukunftsperspektiven.'),
        ),
        missionParagraph(
          textPart('Seine Tante '),
          strongPart('Serife Vural-Banik'),
          textPart(' unterstützt Viva Porta ebenfalls mit ihren langjährigen Führungs- und Leitungserfahrungen. Als Mitglied der Geschäftsleitung in einem Sozialunternehmen bringt sie ausgeprägte organisatorische, strategische und personelle '),
          strongPart('Führungskompetenzen'),
          textPart(' mit. Darüber hinaus verfügt sie durch ihre Ausbildung als Medizinische Fachangestellte über wertvolle Einblicke in den Gesundheits- und Pflegebereich.'),
        ),
      ],
    },
    {
      title: 'Das Fundament',
      paragraphs: [
        missionParagraph(
          textPart('Gemeinsam bringen Wilhelm Josten und Murat Vural fast '),
          strongPart('ein halbes Jahrhundert'),
          textPart(' unternehmerische Erfahrung aus '),
          strongPart('wirtschaftlichen und sozialen'),
          textPart(' Kontexten mit. Dieses Wissen bildet das Fundament von Viva Porta.'),
        ),
        missionParagraph(
          textPart('Doch was uns wirklich auszeichnet, ist unsere Überzeugung: Personaldienstleistung bedeutet mehr als die Vermittlung von Arbeitskräften. Sie bedeutet '),
          strongPart('Vertrauen'),
          textPart('. Sie bedeutet '),
          strongPart('Verantwortung'),
          textPart('. Und sie bedeutet, Menschen auf ihrem Weg zu '),
          strongPart('begleiten'),
          textPart('.'),
        ),
      ],
    },
  ],
  closing: {
    title: 'Denn am Ende sind es immer die Menschen, die den Unterschied machen.',
    paragraphs: [
      missionParagraph(
        textPart('Wir wissen, dass hinter jeder beruflichen Entscheidung Hoffnungen, Träume, Familien und Zukunftspläne stehen. Deshalb behandeln wir jeden Menschen mit Respekt, Aufmerksamkeit und Wertschätzung.'),
      ),
      missionParagraph(
        textPart('Unser Ziel ist es, Unternehmen und Menschen nachhaltig zusammenzubringen - nicht nur für den nächsten Auftrag, sondern für langfristigen Erfolg auf beiden Seiten.'),
      ),
      missionParagraph(
        textPart('Viva Porta steht für partnerschaftliche Zusammenarbeit, Verlässlichkeit und echte Menschlichkeit.'),
      ),
    ],
  },
};

class StaticPageContentRepository {
  private readonly baseContent = landingPageContentRepository.getContent('de');

  // The four industry/branch pages have their applicant- and company-facing copy stored in the
  // opposite maps, so for those keys the audience is flipped to show the correct text. All other pages
  // map 1:1 (applicant => applicantPages, company => pages).
  private static readonly branchKeysWithSwappedCopy = new Set<StaticPageKey>(['pflege', 'gastro', 'handwerk', 'einzelhandel']);

  getPage(key: StaticPageKey, audience: StaticPageAudience = 'applicant'): StaticPageContent {
    const useApplicantMap = StaticPageContentRepository.branchKeysWithSwappedCopy.has(key)
      ? audience === 'company'
      : audience === 'applicant';
    const page = useApplicantMap ? this.applicantPages[key] : this.pages[key];
    const defaults = isIndustryPageKey(key) ? { ...page, ...industryOverviewCopy('de'), heroGallery: industryGalleryDefaults(key, 'de') } : page;
    return audience === 'company' ? industryCompanyHeroDefaults(defaults, 'de') : industryApplicantHeroDefaults(defaults, 'de');
  }

  private readonly applicantPages = createStaticPageApplicantDefaults({
    applicationPortal,
    landingPageContentRepository,
    baseContent: this.baseContent,
    industryCare,
    industryCraft,
    industryGastro,
    industryRetail,
    groupPhoto,
    partnerGlobus,
    globusGemeinsam,
    globusMarkthalle,
    missionImage,
    globusBakery,
    globusButcher,
    globusFreshCounter,

    globusJobCards,
    globusJobsSection,
    globusJobs,
    globusJobsOverview: germanGlobusJobsOverview,
    missionStoryContent,
  });

  private readonly pages = createStaticPageCompanyDefaults({
    applicationPortal,
    landingPageContentRepository,
    baseContent: this.baseContent,
    industryCare,
    industryCraft,
    industryGastro,
    industryRetail,
    groupPhoto,
    partnerGlobus,
    globusGemeinsam,
    globusMarkthalle,
    missionImage,
    globusBakery,
    globusButcher,
    globusFreshCounter,

    globusJobCards,
    globusJobsSection,
    globusJobs,
    globusJobsOverview: germanGlobusJobsOverview,
    missionStoryContent,
  });

}

export const staticPageContentRepository = new StaticPageContentRepository();
